<template>
    <section
        class="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div
            class="flex flex-col gap-2.5 border-b border-zinc-200 p-3 dark:border-gray-700"
        >
            <div class="flex items-center justify-between">
                <span
                    class="text-sm font-semibold text-zinc-950 dark:text-white"
                >
                    Fila
                    <span
                        class="ml-1 font-mono text-xs font-normal text-zinc-500 dark:text-gray-400"
                    >
                        {{ total }}
                    </span>
                </span>
                <div class="flex items-center gap-2">
                    <span
                        class="font-mono text-xs text-zinc-500 dark:text-gray-400"
                        v-tooltip="'J / K navegam na fila'"
                    >
                        J / K
                    </span>
                    <button
                        type="button"
                        class="inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors"
                        :class="
                            filtersOpen || hasCustomFilters
                                ? 'bg-zinc-900 text-white dark:bg-white dark:text-gray-900'
                                : 'text-zinc-600 hover:bg-zinc-100 dark:text-gray-300 dark:hover:bg-gray-700'
                        "
                        @click="filtersOpen = !filtersOpen"
                    >
                        <FunnelIcon class="h-3.5 w-3.5" />
                        Filtros
                    </button>
                </div>
            </div>

            <div
                class="flex h-8 items-center gap-2 rounded-lg border border-zinc-200 px-2.5 dark:border-gray-600"
            >
                <MagnifyingGlassIcon
                    class="h-3.5 w-3.5 shrink-0 text-zinc-400 dark:text-gray-500"
                />
                <input
                    :value="query"
                    type="text"
                    placeholder="Buscar ID ou seller"
                    class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-zinc-950 placeholder:text-zinc-400 focus:ring-0 dark:text-white dark:placeholder:text-gray-500"
                    @input="$emit('update:query', $event.target.value)"
                />
            </div>

            <div class="flex flex-wrap gap-1.5">
                <button
                    v-for="option in sortOptions"
                    :key="option.value"
                    type="button"
                    class="h-[26px] rounded-full border px-2.5 text-xs font-medium transition-colors"
                    :class="
                        sort === option.value
                            ? 'border-zinc-900 bg-zinc-900 text-zinc-50 dark:border-white dark:bg-white dark:text-gray-900'
                            : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    "
                    @click="$emit('update:sort', option.value)"
                >
                    {{ option.label }}
                </button>
            </div>

            <div
                v-if="filtersOpen"
                class="flex flex-col gap-2 rounded-lg bg-zinc-50 p-2.5 dark:bg-gray-900/40"
            >
                <span
                    class="text-xs font-medium text-zinc-500 dark:text-gray-400"
                >
                    Status
                </span>
                <div class="flex flex-wrap gap-1.5">
                    <button
                        v-for="option in statusOptions"
                        :key="option.value"
                        type="button"
                        class="h-[26px] rounded-full border px-2.5 text-xs font-medium transition-colors"
                        :class="
                            draftStatuses.includes(option.value)
                                ? 'border-zinc-900 bg-zinc-900 text-zinc-50 dark:border-white dark:bg-white dark:text-gray-900'
                                : 'border-zinc-200 bg-white text-zinc-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        "
                        @click="toggleDraftStatus(option.value)"
                    >
                        {{ option.label }}
                    </button>
                </div>
                <label
                    class="text-xs font-medium text-zinc-500 dark:text-gray-400"
                    for="queue-min-approved"
                >
                    Mínimo de saques aprovados
                </label>
                <input
                    id="queue-min-approved"
                    v-model="draftMinApproved"
                    type="number"
                    min="0"
                    class="h-8 rounded-md border border-zinc-200 bg-white px-2.5 text-xs text-zinc-950 focus:border-zinc-400 focus:ring-0 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
                <div class="flex justify-end gap-1.5">
                    <button
                        type="button"
                        class="h-7 rounded-md px-2.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        @click="clearFilters"
                    >
                        Limpar
                    </button>
                    <button
                        type="button"
                        class="h-7 rounded-md bg-zinc-900 px-2.5 text-xs font-medium text-white hover:bg-zinc-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                        @click="applyFilters"
                    >
                        Aplicar
                    </button>
                </div>
            </div>
        </div>

        <div
            class="max-h-[calc(100vh-290px)] overflow-y-auto"
            :class="loading ? 'opacity-60' : ''"
        >
            <div
                v-if="loading && !reviews.length"
                class="px-4 py-7 text-center text-[13px] text-zinc-500 dark:text-gray-400"
            >
                Carregando saques…
            </div>

            <button
                v-for="review in reviews"
                :key="review.id"
                :ref="(el) => setItemRef(review.id, el)"
                type="button"
                class="flex w-full flex-col gap-1.5 border-b border-zinc-100 px-3 py-2.5 text-left transition-colors dark:border-gray-700/60"
                :class="
                    selectedId === review.id
                        ? 'bg-zinc-100 shadow-[inset_2px_0_0_#18181b] dark:bg-gray-700/70 dark:shadow-[inset_2px_0_0_#fff]'
                        : 'hover:bg-zinc-50 dark:hover:bg-gray-700/40'
                "
                @click="$emit('select', review)"
            >
                <div class="flex items-center gap-2">
                    <span
                        class="h-2 w-2 shrink-0 rounded-full"
                        :class="riskDotClass(review)"
                        v-tooltip="riskLabel(review)"
                    ></span>
                    <span
                        class="min-w-0 flex-1 truncate text-[13.5px] font-medium text-zinc-950 dark:text-white"
                    >
                        {{ review.cashout?.user?.name }}
                    </span>
                    <span
                        class="text-[13.5px] font-semibold tabular-nums text-zinc-950 dark:text-white"
                    >
                        {{ $root.formatCurrency(review.cashout?.amount) }}
                    </span>
                </div>
                <div
                    class="flex items-center gap-2 pl-4 text-xs text-zinc-500 dark:text-gray-400"
                >
                    <span class="font-mono">#{{ review.cashout?.id }}</span>
                    <span
                        v-if="isSameDayUrgent(review)"
                        class="rounded bg-zinc-900 px-1.5 text-[10px] font-medium text-white dark:bg-white dark:text-gray-900"
                    >
                        Até 12h
                    </span>
                    <span
                        v-if="review.reviews && review.reviews.rejected"
                        class="text-red-600 dark:text-red-400"
                    >
                        {{ review.reviews.rejected }} reprov.
                    </span>
                    <span
                        v-if="skippedIds.includes(review.id)"
                        class="rounded bg-zinc-100 px-1.5 text-[10px] font-medium text-zinc-600 dark:bg-gray-700 dark:text-gray-300"
                    >
                        Pulado
                    </span>
                    <span class="flex-1"></span>
                    <span class="font-mono">{{ requestTime(review) }}</span>
                </div>
            </button>

            <div
                v-if="!loading && !reviews.length"
                class="px-4 py-7 text-center text-[13px] text-zinc-500 dark:text-gray-400"
            >
                Nenhum saque encontrado.
            </div>

            <button
                v-if="hasMore"
                type="button"
                class="w-full px-3 py-2.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:text-gray-300 dark:hover:bg-gray-700/40"
                :disabled="loading"
                @click="$emit('load-more')"
            >
                Carregar mais
            </button>
        </div>
    </section>
</template>

<script>
import { FunnelIcon, MagnifyingGlassIcon } from "@heroicons/vue/24/outline";
import { getRiskBadgeLabel } from "../../../utils/reviewHelpers.js";

const DEFAULT_STATUSES = ["pending", "approved"];

export default {
    name: "ReviewQueuePanel",
    components: {
        FunnelIcon,
        MagnifyingGlassIcon,
    },
    props: {
        reviews: {
            type: Array,
            default: () => [],
        },
        selectedId: {
            type: [Number, String, null],
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        total: {
            type: [Number, String],
            default: 0,
        },
        hasMore: {
            type: Boolean,
            default: false,
        },
        skippedIds: {
            type: Array,
            default: () => [],
        },
        isSameDayUrgent: {
            type: Function,
            required: true,
        },
        query: {
            type: String,
            default: "",
        },
        sort: {
            type: String,
            default: "prazo",
        },
        statuses: {
            type: Array,
            default: () => [...DEFAULT_STATUSES],
        },
        minApproved: {
            type: [String, Number],
            default: "",
        },
    },
    emits: [
        "select",
        "load-more",
        "update:query",
        "update:sort",
        "apply-filters",
    ],
    data() {
        return {
            filtersOpen: false,
            draftStatuses: [...this.statuses],
            draftMinApproved: this.minApproved,
            itemRefs: {},
            sortOptions: [
                { label: "Prazo", value: "prazo" },
                { label: "Maior risco", value: "risco" },
                { label: "Maior valor", value: "valor" },
            ],
            statusOptions: [
                { label: "Solicitado", value: "pending" },
                { label: "Atenção", value: "warning" },
                { label: "Aprovado", value: "approved" },
                { label: "Reprovado", value: "rejected" },
            ],
        };
    },
    computed: {
        hasCustomFilters() {
            return (
                this.minApproved !== "" ||
                this.statuses.length !== DEFAULT_STATUSES.length ||
                DEFAULT_STATUSES.some((status) => !this.statuses.includes(status))
            );
        },
    },
    watch: {
        selectedId(id) {
            this.$nextTick(() => {
                this.itemRefs[id]?.scrollIntoView({ block: "nearest" });
            });
        },
    },
    methods: {
        setItemRef(id, el) {
            if (el) {
                this.itemRefs[id] = el;
            } else {
                delete this.itemRefs[id];
            }
        },
        riskDotClass(review) {
            const score = review?.seller?.score;

            if (score === undefined || score === null) {
                return "bg-zinc-300 dark:bg-gray-500";
            }

            if (score <= 25) {
                return "bg-red-500";
            }

            if (score <= 50) {
                return "bg-amber-500";
            }

            return "bg-green-500";
        },
        riskLabel(review) {
            return getRiskBadgeLabel(review?.seller?.score);
        },
        requestTime(review) {
            const match = String(review?.cashout?.created_at || "").match(
                /(\d{2}:\d{2})/,
            );

            return match ? match[1] : "";
        },
        toggleDraftStatus(value) {
            this.draftStatuses = this.draftStatuses.includes(value)
                ? this.draftStatuses.filter((status) => status !== value)
                : [...this.draftStatuses, value];
        },
        applyFilters() {
            this.$emit("apply-filters", {
                statuses: this.draftStatuses.length
                    ? this.draftStatuses
                    : [...DEFAULT_STATUSES],
                minApproved: this.draftMinApproved ?? "",
            });
            this.filtersOpen = false;
        },
        clearFilters() {
            this.draftStatuses = [...DEFAULT_STATUSES];
            this.draftMinApproved = "";
            this.applyFilters();
        },
    },
};
</script>
